import { Injectable } from '@angular/core';
import type { ReportDescriptorInterface } from '@zaoblako/shared/common/dtos/report/report-descriptor.dto';
import type { EntityCardHistoryQueryInterface } from '@zaoblako/shared/common/dtos/history/entity-card-history-query.dto';
import type { HistoryPageInterface } from '@zaoblako/shared/common/dtos/history/history-page.dto';
import type { CardDescriptorInterface } from '@zaoblako/shared/common/dtos/card/card-descriptor.dto';
import type { ClientDeleteQueryInterface } from '@zaoblako/shared/modules/client/dtos/client/client-delete-query.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { ClientsListPageInterface } from '@zaoblako/shared/modules/client/dtos/client/clients-list-page.dto';
import type { ClientsListQueryInterface } from '@zaoblako/shared/modules/client/dtos/client/clients-list-query.dto';
import type { ClientHelpdeskTasksFormInterface } from '@zaoblako/shared/modules/client/dtos/client/client-helpdesk-tasks-form.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ClientService extends BaseService {
  public baseUrl = '/basis/client/client';

  public getReportDescriptor(): Observable<ReportDescriptorInterface> {
    return this.http.get<ReportDescriptorInterface>(this.baseUrl);
  }

  public getCardDescriptor(): Observable<CardDescriptorInterface> {
    return this.http.get<CardDescriptorInterface>(`${this.baseUrl}/card-descriptor`);
  }

  public findWithPagination(queryParams: ClientsListQueryInterface): Observable<ClientsListPageInterface> {
    return this.http.get<ClientsListPageInterface>(`${this.baseUrl}/items`, { params: this.objectToHttpParams(queryParams) });
  }

  public findAll(queryParams?: ClientsListQueryInterface): Observable<ClientFindInterface[]> {
    return this.http.get<ClientFindInterface[]>(`${this.baseUrl}/all`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public deleteClient(id: number, queryParams?: ClientDeleteQueryInterface): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`, { params: this.objectToHttpParams(queryParams) });
  }

  public restoreOne(id: number): Observable<ClientFindInterface> {
    return this.http.post<ClientFindInterface>(`${this.baseUrl}/${id}/restore`, null);
  }

  public getHistoryById(clientId: number, params: EntityCardHistoryQueryInterface): Observable<HistoryPageInterface> {
    return this.http.get<HistoryPageInterface>(`${this.baseUrl}/${clientId}/history`, {
      params: this.objectToHttpParams(params)
    });
  }

  public parkingBlockForMany(ids: number[]): Observable<ClientFindInterface[]> {
    return this.http.get<ClientFindInterface[]>(`${this.baseUrl}/parking/block`, {
      params: this.objectToHttpParams({ includeIds: ids })
    });
  }

  public parkingBlockForOne(id: number): Observable<ClientFindInterface> {
    return this.http.get<ClientFindInterface>(`${this.baseUrl}/${id}/parking/block`);
  }

  public parkingUnblockForOne(id: number): Observable<ClientFindInterface> {
    return this.http.get<ClientFindInterface>(`${this.baseUrl}/${id}/parking/unblock`);
  }

  public setHelpdeskTasks(clientId: number, body: ClientHelpdeskTasksFormInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${clientId}/helpdesk-tasks`, body);
  }
}
